import { apiClient } from './apiClient';
import { buildCheckListPrintHtml, tituloCheckList } from '../components/CheckListPrintSheet';

const asList = (data) => data?.results || data || [];

// ── Impresión sin pestaña nueva (iframe oculto) ──────────────────────────────

// El logo es una URL firmada que vence a los 5 min: se reutiliza 4 para no
// pedir /empresa/ en cada impresión.
const LOGO_TTL_MS = 4 * 60 * 1000;
let logoCache = null; // { url, ts }

const obtenerLogoEmpresa = async () => {
  if (logoCache && Date.now() - logoCache.ts < LOGO_TTL_MS) return logoCache.url;
  try {
    const res = await apiClient.get('/empresa/');
    const data = res.data;
    const list = data?.results || data || [];
    const first = Array.isArray(list) ? list[0] : list;
    const url = first?.logo || null;
    logoCache = { url, ts: Date.now() };
    return url;
  } catch (_) {
    return null;
  }
};

const obtenerEquipo = (id) => apiClient.get(`/equipos/${id}/`).then((r) => r.data);

export const imprimirCheckList = async ({ checklistId, plantillaId, equipoId, blank, theme }) => {
  let checklist = null;
  let equipo = null;
  let plantilla = null;
  let logoUrl = null;
  const esBlank = Boolean(blank) || Boolean(plantillaId);

  // Todo lo que no depende de otra respuesta se pide a la vez: antes eran
  // check list → equipo → plantilla → logo en serie.
  if (plantillaId) {
    [plantilla, equipo, logoUrl] = await Promise.all([
      checkListService.obtenerPlantilla(plantillaId),
      equipoId ? obtenerEquipo(equipoId) : null,
      obtenerLogoEmpresa(),
    ]);
  } else if (checklistId) {
    [checklist, equipo, logoUrl] = await Promise.all([
      checkListService.obtener(checklistId),
      equipoId ? obtenerEquipo(equipoId) : null,
      obtenerLogoEmpresa(),
    ]);
    const pendientes = await Promise.all([
      equipo ? null : obtenerEquipo(checklist.equipo),
      esBlank ? checkListService.obtenerPlantilla(checklist.plantilla) : null,
    ]);
    equipo = equipo || pendientes[0];
    plantilla = pendientes[1];
  }

  const html = buildCheckListPrintHtml({
    checklist, equipo, plantilla, blank: esBlank, logoUrl, theme,
  });

  // El navegador propone como nombre del PDF el título de la página de arriba
  // (la app, con el número de deploy), no el del iframe. Se cambia mientras
  // dura la impresión y se restaura después.
  const tituloOriginal = document.title;
  const tituloPdf = tituloCheckList({ checklist, equipo, plantilla, blank: esBlank });
  document.title = tituloPdf;

  // 190mm = ancho imprimible de A4 con márgenes de 10mm.
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;top:0;left:-850px;width:190mm;height:1080px;border:0;overflow:hidden';
  document.body.appendChild(frame);

  const removeFrame = () => {
    if (document.title === tituloPdf) document.title = tituloOriginal;
    if (frame.parentNode) frame.parentNode.removeChild(frame);
  };

  frame.contentDocument.open();
  frame.contentDocument.write(html);
  frame.contentDocument.close();

  frame.contentWindow.addEventListener('afterprint', removeFrame);
  // Limpieza de seguridad por si afterprint no dispara
  setTimeout(removeFrame, 120000);
};

export const checkListService = {
  // ── Plantillas ──────────────────────────────────────────────────────────────

  listarPlantillas: async (params = {}) => {
    const response = await apiClient.get('/plantillas-checklist/', { params });
    return asList(response.data);
  },

  obtenerPlantilla: async (id) => {
    const response = await apiClient.get(`/plantillas-checklist/${id}/`);
    return response.data;
  },

  crearPlantilla: async (payload) => {
    const response = await apiClient.post('/plantillas-checklist/', payload);
    return response.data;
  },

  actualizarPlantilla: async (id, payload) => {
    const response = await apiClient.patch(`/plantillas-checklist/${id}/`, payload);
    return response.data;
  },

  eliminarPlantilla: async (id) => {
    await apiClient.delete(`/plantillas-checklist/${id}/`);
  },

  clonarPlantilla: async (id, payload) => {
    const response = await apiClient.post(`/plantillas-checklist/${id}/clonar/`, payload);
    return response.data;
  },

  // ── Puntos de control ────────────────────────────────────────────────────────

  listarPuntos: async (params = {}) => {
    const response = await apiClient.get('/puntos-control/', { params });
    return asList(response.data);
  },

  crearPunto: async (payload) => {
    const response = await apiClient.post('/puntos-control/', payload);
    return response.data;
  },

  actualizarPunto: async (id, payload) => {
    const response = await apiClient.patch(`/puntos-control/${id}/`, payload);
    return response.data;
  },

  eliminarPunto: async (id) => {
    await apiClient.delete(`/puntos-control/${id}/`);
  },

  reordenarPuntos: async (items) => {
    const response = await apiClient.post('/puntos-control/reordenar/', items);
    return response.data;
  },

  // ── Check Lists ──────────────────────────────────────────────────────────────

  listar: async (params = {}) => {
    const response = await apiClient.get('/check-lists/', { params });
    return asList(response.data);
  },

  obtener: async (id) => {
    const response = await apiClient.get(`/check-lists/${id}/`);
    return response.data;
  },

  crear: async (payload) => {
    const response = await apiClient.post('/check-lists/', payload);
    return response.data;
  },

  actualizar: async (id, payload) => {
    const response = await apiClient.patch(`/check-lists/${id}/`, payload);
    return response.data;
  },

  eliminar: async (id) => {
    await apiClient.delete(`/check-lists/${id}/`);
  },

  actualizarItems: async (id, items) => {
    const response = await apiClient.patch(`/check-lists/${id}/items/`, items);
    return response.data;
  },

  finalizar: async (id) => {
    const response = await apiClient.post(`/check-lists/${id}/finalizar/`);
    return response.data;
  },

  generarOtc: async (id) => {
    const response = await apiClient.post(`/check-lists/${id}/generar-otc/`);
    return response.data;
  },

  // ── Fotos ────────────────────────────────────────────────────────────────────

  subirFoto: async (formData) => {
    // Timeout propio: el global (30 s) corta una subida por datos móviles con
    // señal débil aunque esté avanzando, y la foto se perdía.
    const response = await apiClient.post('/checklist-fotos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  eliminarFoto: async (id) => {
    await apiClient.delete(`/checklist-fotos/${id}/`);
  },

  // ── Firmas ───────────────────────────────────────────────────────────────────

  guardarFirma: async (formData) => {
    const response = await apiClient.post('/checklist-firmas/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  actualizarFirma: async (id, formData) => {
    const response = await apiClient.put(`/checklist-firmas/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  eliminarFirma: async (id) => {
    await apiClient.delete(`/checklist-firmas/${id}/`);
  },
};
